import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { useAuth } from "../lib/auth";
import { ArrowUpRight, Search } from "lucide-react";
import { ACCEPT_PAGES, BUSINESS_PAGES } from "../lib/seoContent";
import api from "../lib/api";

const LINKS = [
  { to: "/pricing", label: "pricing", testId: "nav-pricing-link" },
  { to: "/products", label: "products", testId: "nav-products-link" },
  { to: "/accept-crypto-for", label: "for business", testId: "nav-business-link" },
  { to: "/docs", label: "for developers", testId: "nav-docs-link" },
  { to: "/card", label: "cryptocard", testId: "nav-card-link" },
  { to: "/switch", label: "switch", testId: "nav-switch-link" },
];

const MOBILE_PRIMARY = [
  { to: "/", label: "home", testId: "nav-mobile-home" },
  { to: "/products", label: "products", testId: "nav-mobile-products" },
  { to: "/pricing", label: "pricing", testId: "nav-mobile-pricing" },
  { to: "/accept-crypto-for", label: "for business", testId: "nav-mobile-business" },
  { to: "/docs", label: "for developers", testId: "nav-mobile-docs" },
  { to: "/card", label: "cryptocard", testId: "nav-mobile-card", badge: "new" },
  { to: "/switch", label: "switch", testId: "nav-mobile-switch" },
];

const MOBILE_SECONDARY = [
  { to: "/guide", label: "how it works", testId: "nav-mobile-guide" },
  { to: "/guides", label: "merchant guides", testId: "nav-mobile-guides" },
  { to: "/compare", label: "compare gateways", testId: "nav-mobile-compare" },
  { to: "/faq", label: "faq", testId: "nav-mobile-faq" },
];

const SEARCH_INDEX = [
  ...ACCEPT_PAGES.map((p) => ({ label: `accept ${p.name.toLowerCase()}`, alt: p.sym.toLowerCase(), to: `/accept/${p.slug}`, tag: "coin" })),
  ...ACCEPT_PAGES.map((p) => ({ label: `${p.sym.toLowerCase()} to usd`, to: `/convert/${p.sym.toLowerCase()}-to-usd`, tag: "convert" })),
  ...BUSINESS_PAGES.map((b) => ({ label: `for ${b.noun}`, to: `/accept-crypto-for/${b.slug}`, tag: "business" })),
];

// Guide + country entries load on demand when search opens — keeps the large
// guide (~200KB) and country (~75KB) content modules out of the main bundle.
let LAZY_SEARCH_ITEMS = null;
const loadGuideSearchItems = () =>
  LAZY_SEARCH_ITEMS
    ? Promise.resolve(LAZY_SEARCH_ITEMS)
    : Promise.all([import("../lib/guidesContent"), import("../lib/countries")]).then(([g, c]) => {
        LAZY_SEARCH_ITEMS = [
          ...c.COUNTRY_PAGES.map((x) => ({ label: x.name.toLowerCase(), to: `/accept-crypto-in/${x.slug}`, tag: "country" })),
          ...g.GUIDES.map((x) => ({ label: x.title.toLowerCase(), to: `/guides/${x.slug}`, tag: "guide" })),
        ];
        return LAZY_SEARCH_ITEMS;
      });

/**
 * Nav — floating glass capsule: logo · inline links · auth CTA.
 * Mobile: hamburger opens a frosted dropdown panel under the bar.
 */
const searchIndexFor = (q) =>
  q.length >= 2
    ? [...SEARCH_INDEX, ...(LAZY_SEARCH_ITEMS || [])]
        .filter((it) => it.label.includes(q) || (it.alt && it.alt.includes(q)))
        .slice(0, 8)
    : [];

const logSearch = (q, results, source) => {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return;
  api.post("/track/search", { q: s, results, source }).catch(() => {});
};

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const searchBtnRef = useRef(null);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [dq, setDq] = useState("");
  const [guidesLoaded, setGuidesLoaded] = useState(false);
  useEffect(() => {
    if ((searchOpen || menuOpen) && !guidesLoaded) {
      loadGuideSearchItems().then(() => setGuidesLoaded(true)).catch(() => {});
    }
  }, [searchOpen, menuOpen, guidesLoaded]);
  const q = query.trim().toLowerCase();
  const results = searchIndexFor(q);
  const dResults = searchIndexFor(dq.trim().toLowerCase());

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setQuery(""); setSearchOpen(false); setDq(""); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((v) => !v); }
      if (e.key === "Escape") { setSearchOpen(false); setMenuOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    // A11y (QA-005): return focus to the search trigger after the overlay closes.
    if (!searchOpen) return;
    return () => searchBtnRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    // A11y (QA): trap Tab focus inside whichever overlay is open.
    if (!menuOpen && !searchOpen) return;
    const sel = menuOpen ? '#nav-overlay-menu' : '[data-testid="nav-search-dialog"]';
    const onTab = (e) => {
      if (e.key === "Escape") {
        // A11y (QA-006): Escape closes the overlay and restores focus to its trigger.
        e.stopPropagation();
        if (menuOpen) {
          setMenuOpen(false);
          document.querySelector('[data-testid="nav-mobile-toggle"]')?.focus();
        }
        if (searchOpen) setSearchOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const panel = document.querySelector(sel);
      if (!panel) return;
      const foci = panel.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])');
      if (!foci.length) return;
      const first = foci[0], last = foci[foci.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onTab, true);
    return () => document.removeEventListener("keydown", onTab, true);
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    // A11y (QA-002): lock background scroll and hide the sticky mobile CTA
    // while the overlay menu is open.
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.setAttribute("data-nav-open", "1");
    return () => {
      document.body.style.overflow = prev;
      document.body.removeAttribute("data-nav-open");
    };
  }, [menuOpen]);

  useEffect(() => {
    if (q.length < 2) return undefined;
    const t = setTimeout(() => logSearch(q, searchIndexFor(q).length, "mobile"), 1200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const s = dq.trim().toLowerCase();
    if (s.length < 2) return undefined;
    const t = setTimeout(() => logSearch(s, searchIndexFor(s).length, "desktop"), 1200);
    return () => clearTimeout(t);
  }, [dq]);

  const inlineLink =
    "hidden lg:inline text-[13px] font-medium lowercase text-neutral-400 hover:text-white px-3.5 py-2 rounded-full hover:bg-white/5 transition-colors";

  const isHome = location.pathname === "/";

  return (
    <header className={`site-nav ${isHome ? "fixed" : "sticky"} top-0 inset-x-0 z-50 px-3 sm:px-5 pb-2`}>
      <a
        href="#main"
        data-testid="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          const m = document.querySelector("main") || document.querySelector('[data-testid$="-page"]');
          if (m) { m.setAttribute("tabindex", "-1"); m.focus(); m.scrollIntoView(); }
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:bg-white focus:text-black focus:rounded-md focus:px-4 focus:py-2 mono text-[12px] font-bold lowercase"
      >
        skip to content
      </a>
      <div
        className={`max-w-6xl mx-auto rounded-full border transition-colors duration-300 ${
          scrolled || menuOpen
            ? "bg-black/70 border-white/15 backdrop-blur-xl"
            : "bg-black/40 border-white/10 backdrop-blur-xl"
        }`}
      >
        <div className="h-14 pl-5 pr-2 flex items-center justify-between gap-2">
          <Link to="/" data-testid="nav-home-link" className="shrink-0">
            <Logo size="sm" />
          </Link>

          <nav className="hidden lg:flex items-center justify-center flex-1">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={l.testId} className={inlineLink}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              ref={searchBtnRef}
              onClick={() => setSearchOpen(true)}
              data-testid="nav-desktop-search-btn"
              className="hidden lg:inline-flex items-center gap-2 font-mono text-[10px] lowercase text-neutral-500 hover:text-white border border-white/10 hover:border-white/25 rounded-full px-3 h-8 transition-colors mr-1"
            >
              <Search size={11} /> search
              <kbd className="font-mono text-[9px] border border-white/10 rounded px-1 py-px text-neutral-600">⌘k</kbd>
            </button>
            {user && user.is_admin ? (
              <>
                <Link
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="hidden sm:inline text-[13px] font-medium lowercase text-neutral-300 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors"
                >
                  admin
                </Link>
                <button
                  data-testid="nav-logout-button"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="hidden sm:inline-flex items-center bg-white text-black text-[13px] font-bold lowercase px-5 h-10 rounded-full hover:bg-neutral-200 transition-colors"
                >
                  log out
                </button>
              </>
            ) : user && user.username ? (
              <>
                <Link
                  to={`/${user.username}`}
                  data-testid="nav-my-page-link"
                  className="hidden sm:inline text-[13px] font-medium lowercase text-neutral-300 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors"
                >
                  my page
                </Link>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard-link"
                  className="hidden sm:inline-flex items-center bg-white text-black text-[13px] font-bold lowercase px-5 h-10 rounded-full hover:bg-neutral-200 transition-colors"
                >
                  dashboard
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login-link"
                  className="hidden sm:inline text-[13px] font-medium lowercase text-neutral-300 hover:text-white px-4 py-2 rounded-full hover:bg-white/5 transition-colors"
                >
                  log in
                </Link>
                <Link
                  to="/signup"
                  data-testid="nav-get-started-link"
                  className="hidden sm:inline-flex items-center gap-1.5 bg-white text-black text-[13px] font-bold lowercase px-5 h-10 rounded-full hover:bg-neutral-200 transition-colors"
                >
                  get started
                </Link>
              </>
            )}

            <button
              type="button"
              data-testid="nav-mobile-toggle"
              aria-label={menuOpen ? "close menu" : "open menu"}
              aria-expanded={menuOpen}
              aria-controls="nav-overlay-menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-neutral-300 hover:text-white hover:border-white/40 transition-colors"
            >
              <span className="flex flex-col gap-[4px] w-[15px]">
                <span className={`h-[1.5px] bg-current transition-transform duration-300 ${menuOpen ? "translate-y-[5.5px] rotate-45" : ""}`} />
                <span className={`h-[1.5px] bg-current transition-opacity duration-200 ${menuOpen ? "opacity-0" : ""}`} />
                <span className={`h-[1.5px] bg-current transition-transform duration-300 ${menuOpen ? "-translate-y-[5.5px] -rotate-45" : ""}`} />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile / tablet dropdown panel ─────────────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="nav-panel"
            id="nav-overlay-menu"
            role="dialog"
            aria-modal="true"
            aria-label="site menu"
            data-testid="nav-overlay-menu"
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden max-w-6xl mx-auto mt-2 rounded-3xl bg-black/85 backdrop-blur-2xl border border-white/15 overflow-hidden flex flex-col max-h-[calc(100svh-110px)]"
          >
            <nav className="px-5 pt-5 pb-3 flex-1 min-h-0 overflow-y-auto">
              <div className="relative mb-4">
                <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600 pointer-events-none" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="nav-mobile-search-input"
                  placeholder="search coins, countries, guides…"
                  className="w-full h-11 bg-white/[0.05] border border-white/10 focus:border-white/30 rounded-full pl-10 pr-4 text-[13px] lowercase text-white placeholder:text-neutral-600 outline-none transition-colors"
                />
              </div>
              {q.length >= 2 ? (
                <div data-testid="nav-mobile-search-results">
                  {results.length === 0 && (
                    <div className="px-1 py-3 font-mono text-[11px] text-neutral-600">no matches — try "bitcoin", "india", "invoice"…</div>
                  )}
                  {results.map((r) => (
                    <Link
                      key={r.to}
                      to={r.to}
                      data-testid="nav-mobile-search-result"
                      className="group flex items-center gap-3 py-3 px-1 border-b border-white/[0.07]"
                    >
                      <span className="flex-1 min-w-0 truncate text-[14px] font-bold lowercase tracking-tight text-neutral-200 group-hover:text-white transition-colors">{r.label}</span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-600 shrink-0">{r.tag}</span>
                      <ArrowUpRight size={13} className="text-neutral-700 group-hover:text-white transition-colors shrink-0" />
                    </Link>
                  ))}
                </div>
              ) : (
                <>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-600 px-1">navigate</div>
              <div className="mt-1.5">
                {MOBILE_PRIMARY.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.035, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Link
                      to={l.to}
                      data-testid={l.testId}
                      className="group flex items-center gap-4 py-3 px-1 border-b border-white/[0.07]"
                    >
                      <span className="font-mono text-[10px] text-neutral-700 group-hover:text-neutral-400 transition-colors shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 min-w-0 text-[19px] font-black lowercase tracking-tight leading-none text-neutral-200 group-hover:text-white transition-colors">
                        {l.label}
                        {l.badge && (
                          <span className="ml-2.5 align-middle font-mono text-[8px] font-bold uppercase tracking-[0.25em] bg-white text-black px-1.5 py-0.5">
                            {l.badge}
                          </span>
                        )}
                      </span>
                      <ArrowUpRight size={14} className="text-neutral-700 group-hover:text-white transition-colors shrink-0" />
                    </Link>
                  </motion.div>
                ))}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-600 px-1 mt-6">learn</div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-6">
                {MOBILE_SECONDARY.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.035, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Link
                      to={l.to}
                      data-testid={l.testId}
                      className="group flex items-center justify-between gap-3 py-2.5 px-1 border-b border-white/[0.07]"
                    >
                      <span className="min-w-0 truncate text-[13px] font-medium lowercase text-neutral-400 group-hover:text-white transition-colors">
                        {l.label}
                      </span>
                      <ArrowUpRight size={12} className="text-neutral-700 group-hover:text-white transition-colors shrink-0" />
                    </Link>
                  </motion.div>
                ))}
              </div>
                </>
              )}
            </nav>
            <div className="p-4 pt-3 flex flex-col gap-2.5 border-t border-white/10 shrink-0">
              {user && user.username ? (
                <>
                  <Link
                    to={user.is_admin ? "/admin" : "/dashboard"}
                    data-testid="nav-mobile-dashboard"
                    className="inline-flex items-center justify-center bg-white text-black text-[13px] font-bold lowercase h-12 rounded-full hover:bg-neutral-200 transition-colors"
                  >
                    {user.is_admin ? "admin" : "dashboard"}
                  </Link>
                  <button
                    data-testid="nav-mobile-logout"
                    onClick={async () => { await logout(); navigate("/"); }}
                    className="inline-flex items-center justify-center border border-white/20 text-white text-[13px] font-medium lowercase h-12 rounded-full hover:bg-white/10 transition-colors"
                  >
                    log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    data-testid="nav-mobile-signup-button"
                    className="inline-flex items-center justify-center bg-white text-black text-[13px] font-bold lowercase h-12 rounded-full hover:bg-neutral-200 transition-colors"
                  >
                    claim your page
                  </Link>
                  <Link
                    to="/login"
                    data-testid="nav-mobile-login"
                    className="inline-flex items-center justify-center border border-white/20 text-white text-[13px] font-medium lowercase h-12 rounded-full hover:bg-white/10 transition-colors"
                  >
                    log in
                  </Link>
                </>
              )}
              <div className="pt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 text-center" data-testid="nav-mobile-meta">
                0% transaction fee · non-custodial · 19 coins
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {searchOpen && (
        <div
          data-testid="nav-search-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="site search"
          className="fixed inset-0 z-[100] flex items-start justify-center pt-[16vh] px-4"
          onClick={() => setSearchOpen(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg bg-black border border-white/15 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative border-b border-white/10">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600 pointer-events-none" />
              <input
                autoFocus
                value={dq}
                onChange={(e) => setDq(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && dResults[0]) { navigate(dResults[0].to); setSearchOpen(false); setDq(""); }
                }}
                data-testid="nav-search-input"
                placeholder="search coins, countries, guides…"
                className="w-full h-12 bg-transparent pl-11 pr-14 text-[14px] lowercase text-white placeholder:text-neutral-600 outline-none"
              />
              <kbd className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-[9px] uppercase text-neutral-600 border border-white/10 rounded px-1.5 py-0.5">esc</kbd>
            </div>
            <div className="max-h-[320px] overflow-y-auto p-2" data-testid="nav-search-results">
              {dq.trim().length < 2 ? (
                <div className="px-3 py-4 font-mono text-[11px] text-neutral-600">type 2+ letters — try "btc", "india", "invoice"…</div>
              ) : dResults.length === 0 ? (
                <div className="px-3 py-4 font-mono text-[11px] text-neutral-600">no matches.</div>
              ) : (
                dResults.map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    onClick={() => { setSearchOpen(false); setDq(""); }}
                    data-testid="nav-search-result"
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <span className="flex-1 min-w-0 truncate text-[13.5px] font-bold lowercase tracking-tight text-neutral-200 group-hover:text-white transition-colors">{r.label}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-600 shrink-0">{r.tag}</span>
                    <ArrowUpRight size={12} className="text-neutral-700 group-hover:text-white transition-colors shrink-0" />
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
